import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Shield, Loader2, AlertTriangle, Info, AlertCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const SEVERITY_STYLES = {
  info: { cls: "bg-blue-100 text-blue-700", icon: Info },
  warning: { cls: "bg-yellow-100 text-yellow-700", icon: AlertTriangle },
  critical: { cls: "bg-rose-100 text-rose-700", icon: AlertCircle },
};

const EVENT_LABELS = {
  login: "Login", logout: "Logout", failed_login: "Failed Login",
  card_added: "Card Added", card_deleted: "Card Deleted", token_accessed: "Token Accessed",
  key_created: "Key Created", key_revoked: "Key Revoked", settings_changed: "Settings Changed",
  unauthorized_access: "Unauthorized Access",
};

export default function SecurityAudit() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    base44.entities.SecurityEvent.list("-created_date", 200).then(setEvents).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? events : events.filter((e) => e.severity === filter);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Security Audit</h1>
          <p className="text-sm text-slate-500 mt-0.5">Historical system events and admin changes</p>
        </div>
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {["all", "info", "warning", "critical"].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition capitalize ${filter === s ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Shield className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>No security events recorded.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {filtered.map((event) => {
            const sev = SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.info;
            const SevIcon = sev.icon;
            return (
              <div key={event.id} className="flex items-start gap-4 px-5 py-4">
                <div className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${sev.cls}`}><SevIcon className="h-3.5 w-3.5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-slate-900">{EVENT_LABELS[event.event_type] || event.event_type}</span>
                    <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${sev.cls}`}>{event.severity}</span>
                  </div>
                  <p className="text-sm text-slate-600">{event.description || "No description"}</p>
                  {event.ip_address && <p className="text-xs text-slate-400 mt-0.5">IP: {event.ip_address}</p>}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-slate-400">{event.created_date ? formatDistanceToNow(new Date(event.created_date), { addSuffix: true }) : "—"}</p>
                  {event.resolved !== undefined && (
                    <span className={`text-[10px] font-medium ${event.resolved ? "text-emerald-600" : "text-rose-500"}`}>
                      {event.resolved ? "Resolved" : "Open"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}